import {
  Box,
  Button,
  ButtonGroup,
  FormControlLabel,
  FormHelperText,
  Slider,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { TITLE_1_PROGRAM_OPTIONS } from 'routes/highschools/title-one'
import { ReligiousAffiliation, UIFilters } from 'routes/highschools/types'

import { numericValueOrDefault, parseNumeric } from '.'
import { PremiumWrapper } from '../PremiumPreview/PremiumWrapper'
import FilterReligiousAffiliation from './FilterReligiousAffiliation'

const useStyles = makeStyles(() => ({
  buttonGroup: {
    '& .MuiButton-outlined.Mui-disabled': {
      // Override default styles in disabled case so that it pops more under the premium preview treatment
      opacity: 1,
    },
  },
}))

type FilterSchoolDetailsProps = {
  schoolType?: 'public' | 'private'
  classSize?: {
    min?: number
    max?: number
  }
  religiousAffiliation?: ReligiousAffiliation[]
  isActive?: boolean
  isTarget?: boolean
  isTitle1Eligible?: boolean
  title1Program?: 'swp' | 'tas'
  pctFreeReducedLunch?: {
    min?: number
    max?: number
  }
  onChange: (formState: Partial<UIFilters>) => void
  errors: {
    classSize?: {
      helperText: string
    }
  }
  enabled: boolean
}

const FilterSchoolDetails = ({
  schoolType,
  classSize,
  religiousAffiliation,
  isActive,
  isTarget,
  isTitle1Eligible,
  title1Program,
  pctFreeReducedLunch,
  onChange,
  errors,
  enabled,
}: FilterSchoolDetailsProps) => {
  const classes = useStyles()
  return (
    <Box
      display="flex"
      flexDirection="column"
      gridGap={24}
    >
      <Typography
        component="div"
        variant="h6"
      >
        School Details
      </Typography>
      <Box
        display="flex"
        flexDirection="column"
        gridGap={16}
      >
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={isActive ?? false}
              onChange={(_, checked) => onChange({ isActive: checked ? true : undefined })}
            />
          }
          label="Only show Scoir schools"
        />
      </Box>
      <PremiumWrapper
        enabled={enabled}
        trackingIdPlacement="highschool-details-filter"
        paddingOffset={12}
      >
        <Box
          display="flex"
          flexDirection="column"
          gridGap={16}
        >
          <FormControlLabel
            control={
              <Switch
                color="primary"
                disabled={!enabled}
                checked={isTarget ?? false}
                onChange={(_, checked) => onChange({ isTarget: checked ? true : undefined })}
              />
            }
            label="Only show target schools"
          />
        </Box>
      </PremiumWrapper>
      <Box
        display="flex"
        flexDirection="column"
        gridGap={16}
      >
        <Typography variant="subtitle2">Type</Typography>
        <ButtonGroup
          disableFocusRipple
          color="primary"
        >
          <Button
            variant={schoolType === 'public' ? 'contained' : 'outlined'}
            onClick={() =>
              schoolType === 'public' ? onChange({ schoolType: undefined }) : onChange({ schoolType: 'public' })
            }
          >
            Public
          </Button>
          <Button
            variant={schoolType === 'private' ? 'contained' : 'outlined'}
            onClick={() =>
              schoolType === 'private' ? onChange({ schoolType: undefined }) : onChange({ schoolType: 'private' })
            }
          >
            Private
          </Button>
        </ButtonGroup>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        gridGap={16}
      >
        <FilterReligiousAffiliation
          religiousAffiliation={religiousAffiliation || []}
          setReligiousAffiliation={(affiliations: ReligiousAffiliation[]) => {
            onChange({ religiousAffiliation: affiliations.length > 0 ? affiliations : undefined })
          }}
        />
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        gridGap={16}
      >
        <Typography variant="subtitle2">Title 1 Eligibility</Typography>
        <ButtonGroup
          disableFocusRipple
          color="primary"
        >
          <Button
            variant={isTitle1Eligible === true ? 'contained' : 'outlined'}
            onClick={() =>
              isTitle1Eligible === true
                ? onChange({ isTitle1Eligible: undefined })
                : onChange({ isTitle1Eligible: true })
            }
          >
            Eligible
          </Button>
          <Button
            variant={isTitle1Eligible === false ? 'contained' : 'outlined'}
            onClick={() =>
              isTitle1Eligible === false
                ? onChange({ isTitle1Eligible: undefined })
                : onChange({ isTitle1Eligible: false })
            }
          >
            Not Eligible
          </Button>
        </ButtonGroup>
      </Box>
      <PremiumWrapper
        enabled={enabled}
        trackingIdPlacement="highschool-details-filter"
      >
        <Box
          display="flex"
          flexDirection="column"
          gridGap={16}
        >
          <Typography
            component="div"
            variant="subtitle2"
          >
            Title 1 Program
          </Typography>
          <ButtonGroup
            disableFocusRipple
            className={classes.buttonGroup}
            color="primary"
          >
            {Object.values(TITLE_1_PROGRAM_OPTIONS).map((opt) => (
              <Button
                disabled={!enabled}
                key={opt.value}
                variant={opt.value === title1Program ? 'contained' : 'outlined'}
                onClick={() =>
                  opt.value === title1Program
                    ? onChange({ title1Program: undefined })
                    : onChange({ title1Program: opt.value })
                }
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          gridGap={16}
        >
          <Typography
            component="div"
            variant="subtitle2"
          >
            % Free or Reduced Lunch
          </Typography>
          <Box
            pl={1}
            pr={2}
          >
            <Slider
              disabled={!enabled}
              value={[pctFreeReducedLunch?.min || 0, pctFreeReducedLunch?.max || 100]}
              onChange={(_, value) => {
                onChange({
                  pctFreeReducedLunch: {
                    min: (value as number[])[0] === 0 ? undefined : (value as number[])[0],
                    max: (value as number[])[1] === 100 ? undefined : (value as number[])[1],
                  },
                })
              }}
              getAriaValueText={(value: number) => `${value} percent`}
              getAriaLabel={(i) => {
                if (i === 0) return 'percent free or reduced lunch minimum'
                return 'percent free or reduced lunch maximum'
              }}
              valueLabelDisplay="auto"
              marks={[
                {
                  value: 0,
                  label: '0%',
                },
                {
                  value: 100,
                  label: '100%',
                },
              ]}
            />
          </Box>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          gridGap={16}
        >
          <Typography
            component="div"
            variant="subtitle2"
          >
            Senior Class Size
          </Typography>
          <Box>
            <Box
              display="flex"
              gridGap={16}
            >
              <Box flexBasis="50%">
                <TextField
                  disabled={!enabled}
                  fullWidth
                  id="min-class-size"
                  variant="outlined"
                  color="primary"
                  type="number"
                  label="Min"
                  value={numericValueOrDefault(classSize?.min)}
                  inputProps={{ min: 1, max: classSize?.max }}
                  onChange={(e) => {
                    const value = parseNumeric(e.target.value)
                    onChange({
                      classSize: {
                        ...classSize,
                        min: value,
                      },
                    })
                  }}
                  error={!!errors.classSize}
                />
              </Box>
              <Box flexBasis="50%">
                <TextField
                  disabled={!enabled}
                  fullWidth
                  id="max-class-size"
                  variant="outlined"
                  color="primary"
                  type="number"
                  label="Max"
                  value={numericValueOrDefault(classSize?.max)}
                  inputProps={{ min: classSize?.min || 0 }}
                  onChange={(e) => {
                    const value = parseNumeric(e.target.value)
                    onChange({
                      classSize: {
                        ...classSize,
                        max: value,
                      },
                    })
                  }}
                  error={!!errors.classSize}
                />
              </Box>
            </Box>
            <FormHelperText error={!!errors.classSize}>{errors.classSize?.helperText}</FormHelperText>
          </Box>
        </Box>
      </PremiumWrapper>
    </Box>
  )
}

export default FilterSchoolDetails
